/**
 * De stand van een project in pure stukjes, uit data die de projectpagina al heeft
 * (ProjectDataContext + de flow). Basis voor "Volgende stap" en "Wat er is".
 *
 * Ontbrekende data is expliciet `onbekend` / null — nooit een stille nul: een regel die
 * op onbekende data zou vuren, slaat zichzelf over.
 */
import { isTaakAfgerond } from '../../ProjectOverview/parts/projectMeta';
import { alsDag, dagenTot } from './datums';

// Kalenderblokken staan in de takentabel maar zijn geen taak (CLAUDE.md, Taken-statussen).
export const AGENDA_TYPES = new Set(['Afspraak', 'Vrij/intern', 'Vrije dag', 'Draaidag', 'Vanallesuurtje']);
// Wachten op een ander: niet "over datum" voor de PL.
const WACHT = new Set(['Wacht op feedback', 'Nog niet van toepassing']);

/** "€ 24.300" of "€ 8.855,50" (centen alleen als ze er zijn). */
export function geld(n) {
  const x = Number(n);
  const heel = Math.round(x * 100) % 100 === 0;
  return `€ ${x.toLocaleString('nl-NL', { minimumFractionDigits: heel ? 0 : 2, maximumFractionDigits: heel ? 0 : 2 })}`;
}

export const isAgendaBlok = (t) => AGENDA_TYPES.has(t?.fields?.Type) || AGENDA_TYPES.has(t?.fields?.PrioNr);

/** De datum waarop een taak af moet: eind → deadline → start. */
export function taakEind(task) {
  const f = task?.fields || {};
  return alsDag(f.EndDateUitvoerder || f.Deadline || f.StartDateUitvoerder);
}

/** Open echte taken (geen agenda-blokken, niet gearchiveerd), vroegste datum eerst. */
export function openTaken(tasks = []) {
  const sleutel = (t) => { const d = taakEind(t); return d ? d.getTime() : Infinity; };
  return (tasks || [])
    .filter((t) => !isTaakAfgerond(t) && !isAgendaBlok(t))
    .sort((a, b) => sleutel(a) - sleutel(b));
}

/** Open taken waarvan de einddatum voorbij is (wachtende taken tellen niet). */
export function takenOverDatum(tasks = [], vandaag) {
  return openTaken(tasks).filter((t) => {
    if (WACHT.has(t.fields?.PrioNr)) return false;
    const n = dagenTot(taakEind(t), vandaag);
    return n !== null && n < 0;
  });
}

/** Eerste echte naam van de uitvoerder (geen rec-id's), of ''. */
export function taakEigenaar(task) {
  const f = task?.fields || {};
  const raw = f['Voornaam Assignee'] || f.Assignee || [];
  const lijst = (Array.isArray(raw) ? raw : [raw]).filter((n) => n && !String(n).startsWith('rec'));
  return lijst.length ? String(lijst[0]) : '';
}

/**
 * Offerte-stand uit de offerteregels (zelfde bron als het tabblad):
 * - 'open'     regels van de Gripp-ófferte (nog geen opdracht = nog niet geaccepteerd)
 * - 'akkoord'  regels op de opdracht / gekoppeld aan het project
 * - 'geen'     geladen, maar er hangen geen regels aan
 * - 'onbekend' nog aan het laden, of lezen mislukt
 */
export function offerteStand(quoteLines, { laden = false, fout = false } = {}) {
  if (fout || laden || !quoteLines) return { stand: 'onbekend', bedrag: null, regels: 0 };
  const regels = Number(quoteLines.count) || 0;
  const bedrag = typeof quoteLines.total_price === 'number' ? quoteLines.total_price : null;
  if (!regels) return { stand: 'geen', bedrag: null, regels: 0 };
  return {
    stand: quoteLines.source === 'gripp_offer' ? 'open' : 'akkoord',
    bedrag,
    regels,
    gewijzigd: quoteLines.gripp_updated_on || null,
  };
}

/** Een draaidag die echt doorgaat (4-laags filter, zie CLAUDE.md "Draaidagen"). */
export function isGeldigeDraaidag(d) {
  const f = d?.fields || {};
  return !(f.is_valid === false || f.all_day === false || f.hidden === true || f.outlook_link_status === false);
}

/** Datum van een draaidag: de pure StartDatum, anders de Outlook-start. */
export function draaidagDatum(d) {
  const f = d?.fields || {};
  return alsDag(f.StartDatum || f.StartdatumOutlook);
}

export function heeftCallsheet(d) {
  const f = d?.fields || {};
  return (Array.isArray(f.CallSheet) && f.CallSheet.length > 0) || Number(f['Aantal callsheets']) > 0;
}

export function callsheetNietNodig(d) {
  return /^nee/i.test(String(d?.fields?.['Callsheet nodig?'] || '').trim());
}

/** Geldige draaidagen op datum, met { verleden, komend }. */
export function draaidagStand(draaidagen = [], vandaag) {
  const geldig = (draaidagen || []).filter(isGeldigeDraaidag)
    .map((d) => ({ d, dag: draaidagDatum(d) }))
    .sort((a, b) => (a.dag ? a.dag.getTime() : Infinity) - (b.dag ? b.dag.getTime() : Infinity));
  const verleden = geldig.filter((x) => x.dag && dagenTot(x.dag, vandaag) < 0);
  const komend = geldig.filter((x) => x.dag && dagenTot(x.dag, vandaag) >= 0);
  return { geldig, verleden, komend };
}

export const deliverableNaam = (d) => d?.fields?.['Naam van Deliverable'] || d?.fields?.Deliverable || d?.name || 'Deliverable';
export const deliverableStatus = (d) => String(d?.fields?.Status || '').trim();

/** Is dit adres van ons (geen klantmail)? */
export const isEigenAdres = (adres) => /@(fullframe\.nl|full-frame\.nl)\b/i.test(String(adres || ''));

/**
 * Samenvatting van de mails rond het project (lijst uit project-correspondence,
 * nieuwste eerst). null = nog niet geladen / niet beschikbaar.
 */
export function mailStand(emails) {
  if (!Array.isArray(emails)) return null;
  const laatste = emails[0] || null;
  return {
    aantal: emails.length,
    laatsteDatum: laatste?.datum || null,
    laatsteVanKlant: laatste ? !isEigenAdres(laatste.van) : null,
  };
}
